import React, { useState, useMemo } from 'react';
import { styled } from '@linaria/react';

import { useModal } from 'hooks/index.js';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { useFeatureContext } from 'contexts/FeatureContext.jsx';
import {
  CLIENT_PRODUCT,
  ON_DEMAND_PRODUCT,
  ONE_TO_MANY_PRODUCT,
  ONE_TO_ONE_PRODUCT,
} from 'constants/products.js';
import ProfileInfo from './ProfileInfo.jsx';
import UserLanguage from './UserLanguage.jsx';
import AccountAndSecurity from './AccountAndSecurity.jsx';
import CloseConfirmationDialog from './CloseConfirmationDialog.jsx';
import NavBar from './NavBar.jsx';
import { trackingService } from 'services/tracking/TrackingService.js';
import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';

const Wrapper = styled.div`
  min-height: 546px;
  display: grid;
  grid-template-columns: 220px 1fr;
  grid-template-rows: 1fr;
  column-gap: 40px;
`;

const Content = styled.div`
  grid-area: 1 / 2 / 2 / 3;
`;

const orgUserItems = [
  {
    id: 'profileInfo',
    title: 'Profile info',
    translationKey: 'profile.profileSectionTitle',
    component: ProfileInfo,
  },
  {
    id: 'accountAndSecurity',
    title: 'Account & security',
    translationKey: 'profile.accountAndSecuritySectionTitle',
    component: AccountAndSecurity,
  },
  {
    id: 'language',
    title: 'Language',
    translationKey: 'profile.languageLabel',
    component: UserLanguage,
    requiredFeature: 'userLanguageEnabled',
    isBeta: true,
  },
];

const defaultUserItems = [
  {
    id: 'accountAndSecurity',
    title: 'Account & security',
    translationKey: 'profile.accountAndSecuritySectionTitle',
    component: AccountAndSecurity,
  },
];


const AccountSettings = ({ isClosable, setIsClosable, selectedSectionId }) => {
  const { isOpen: isConfirmDialogOpen, open: openConfirmDialog, close: closeConfirmDialog } = useModal();
  const { selectedProduct } = useProductDataOutsideRoutes();
  const { features } = useFeatureContext();
  const { userLanguageEnabled } = features;
  const { t } = useLocalization();
  const workspaceProducts = [CLIENT_PRODUCT, ONE_TO_ONE_PRODUCT, ONE_TO_MANY_PRODUCT, ON_DEMAND_PRODUCT];
  const items = useMemo(() => {
    return (workspaceProducts.includes(selectedProduct) ? orgUserItems : defaultUserItems).map(item => ({
      ...item,
      title: t(item.translationKey),
    })).filter(item => {
      if (!item.requiredFeature) return true;
      return features[item.requiredFeature];
    });
  }, [selectedProduct, t, features]);
  const [selectedItemId, setSelectedItemId] = useState(selectedSectionId || items[0].id);
  const [nextSelectedItemId, setNextSelectedItemId] = useState(null);

  const handleItemSelection = (id) => {
    if (!isClosable) {
      setNextSelectedItemId(id);
      openConfirmDialog();
      return;
    }
    trackingService.track({
      eventType: `profile-modal:navigation:${id}`,
    });
    setSelectedItemId(id);
  };

  const handleConfirmCancelClick = () => {
    closeConfirmDialog();
  };

  const handleConfirmDiscardClick = () => {
    trackingService.track({
      eventType: 'profile-modal:discard-updates',
      value: selectedItemId,
    });
    closeConfirmDialog();
    setIsClosable(true);
    trackingService.track({
      eventType: 'profile-modal:navigate',
      value: nextSelectedItemId,
    });
    setSelectedItemId(nextSelectedItemId);
  };

  return (
    <>
      <Wrapper>
        <NavBar items={items} selectedItem={selectedItemId} setSelectedItem={handleItemSelection} />
        <Content>
          {selectedItemId === 'profileInfo' && (
            <ProfileInfo
              setIsClosable={setIsClosable}
              role="tabpanel"
              id="profileInfo"
              aria-expanded={selectedItemId === 'profileInfo'}
            />
          )}
          {selectedItemId === 'accountAndSecurity' && (
            <AccountAndSecurity
              setIsClosable={setIsClosable}
              role="tabpanel"
              id="accountAndSecurity"
              aria-expanded={selectedItemId === 'accountAndSecurity'}
            />
          )}
          {userLanguageEnabled && selectedItemId === 'language' && (
            <UserLanguage
              role="tabpanel"
              id="language"
              aria-expanded={selectedItemId === 'language'}
            />
          )}
        </Content>
      </Wrapper>
      <CloseConfirmationDialog
        handleConfirmCancelClick={handleConfirmCancelClick}
        handleConfirmDiscardClick={handleConfirmDiscardClick}
        isConfirmDialogOpen={isConfirmDialogOpen}
      />
    </>
  );
};

export default AccountSettings;
